/**
 * composeOperatorState — derives canonical OperatorState from three lanes.
 *
 * Derivation rules: 10-decision-state-model-and-home-scope.md §1
 * Lane sources:     21-launch-implementation-plan.md §Architecture split
 *
 * This function is pure: given the same lane inputs, it always returns
 * the same OperatorState. No side effects, no external dependencies.
 *
 * @param {object} plugin       - Result from readPluginLane
 * @param {object} browserLocal - Result from readBrowserLocalLane
 * @param {object} gateway      - Result from readGatewayLane
 * @returns {object} Canonical OperatorState
 */

import {
  ConnectionStatus,
  IndexingStatus,
  PreviewBuildStatus,
  PreviewBrowserStatus,
  LiveStatus,
  PublishState,
  IntegrationHealth,
  DiagnosticsSeverity,
} from '../operatorState';
import { computeNextAction } from '../computeNextAction';

// ---------------------------------------------------------------------------
// Individual field derivation rules (doc 10 §1)
// ---------------------------------------------------------------------------

function deriveConnection(plugin) {
  if (!plugin.available) {
    return ConnectionStatus.ERROR;
  }
  if (plugin.auth.connected && plugin.auth.hasToken) {
    return ConnectionStatus.CONNECTED;
  }
  return ConnectionStatus.NOT_CONNECTED;
}

function deriveIndexing(plugin) {
  if (!plugin.available) {
    return IndexingStatus.IDLE;
  }
  // Bridge v2+ authoritative signal: can_search=true means index is healthy (Task 10O AC1)
  if (plugin.canSearch === true) return IndexingStatus.READY;

  const { status } = plugin.indexing;

  // Map plugin indexing status → canonical IndexingStatus
  if (status === 'done' || status === 'completed') return IndexingStatus.READY;
  if (status === 'running' || status === 'queued') return IndexingStatus.RUNNING;
  if (status === 'error' || status === 'failed') return IndexingStatus.ERROR;
  return IndexingStatus.IDLE;
}

function deriveMode(plugin, gateway) {
  return gateway.mode || plugin.onboarding?.selectedExperience || null;
}

function derivePreviewBuild(plugin) {
  // When plugin provides ui.preview_build_status (via onboarding.state),
  // map it to canonical PreviewBuildStatus.
  const raw = plugin.previewBuildStatus;
  if (raw === 'ready') return PreviewBuildStatus.READY;
  if (raw === 'building') return PreviewBuildStatus.BUILDING;
  if (raw === 'error') return PreviewBuildStatus.ERROR;
  if (raw === 'none') return PreviewBuildStatus.NOT_READY;

  // Older plugins (app.bootstrap fallback) do not expose this field.
  return PreviewBuildStatus.UNKNOWN;
}

function derivePreviewBrowser(browserLocal) {
  if (!browserLocal.available) {
    return PreviewBrowserStatus.UNKNOWN;
  }

  const { previewMode, previewFeedback, feedbackStale } = browserLocal;

  // Check feedback errors first
  if (previewFeedback && previewFeedback.errors && previewFeedback.errors.length > 0) {
    return PreviewBrowserStatus.ERROR;
  }

  // Preview enabled with fresh feedback
  if (previewMode === 'enabled' && previewFeedback && !feedbackStale) {
    return PreviewBrowserStatus.ENABLED;
  }

  // Preview enabled but feedback is stale or missing
  if (previewMode === 'enabled' && (feedbackStale || !previewFeedback)) {
    return PreviewBrowserStatus.STALE;
  }

  // Preview explicitly disabled
  if (previewMode === 'disabled') {
    return PreviewBrowserStatus.DISABLED;
  }

  // No preview data at all
  return PreviewBrowserStatus.UNKNOWN;
}

function deriveLive(plugin, gateway, connection) {
  if (plugin.liveState && typeof plugin.liveState === 'object') {
    if (plugin.liveState.reason === 'module_disabled') {
      return LiveStatus.ERROR;
    }
    if (plugin.liveState.enabled !== true) {
      return LiveStatus.NOT_ACTIVE;
    }
    if (plugin.publishState === PublishState.NOT_PUBLISHED) {
      return LiveStatus.NOT_ACTIVE;
    }
    if (connection === ConnectionStatus.ERROR) {
      return LiveStatus.ERROR;
    }
    if (connection === ConnectionStatus.CONNECTED) {
      return LiveStatus.ACTIVE;
    }
    const liveConfirmed = plugin.canSearch === true || plugin.activeIndexPresent === true;
    if (liveConfirmed && connection === ConnectionStatus.CONNECTED) {
      return LiveStatus.ACTIVE;
    }
    return LiveStatus.UNKNOWN;
  }

  const publishedBundle = plugin.publishState === PublishState.PUBLISHED
    || plugin.publishState === PublishState.UNPUBLISHED_CHANGES;
  const liveConfirmed = plugin.canSearch === true || plugin.activeIndexPresent === true;
  if (publishedBundle && liveConfirmed && connection === ConnectionStatus.CONNECTED) {
    return LiveStatus.ACTIVE;
  }

  // searchVisible=false is a definitive gateway setting: search is off.
  if (!gateway.searchVisible) {
    return LiveStatus.NOT_ACTIVE;
  }
  // searchVisible=true + connection error → error.
  if (connection === ConnectionStatus.ERROR) {
    return LiveStatus.ERROR;
  }
  // When plugin confirms search is live — either via can_search (bridge v2+)
  // or activeIndexPresent (legacy field). Both are plugin-confirmed live truth.
  if (liveConfirmed && connection === ConnectionStatus.CONNECTED) {
    return LiveStatus.ACTIVE;
  }
  // Degrade to UNKNOWN for older plugins or when active index is absent/unknown.
  return LiveStatus.UNKNOWN;
}

function deriveIntegrationHealth(mode, browserLocal) {
  if (!mode) {
    return IntegrationHealth.UNKNOWN;
  }

  if (!browserLocal.available || !browserLocal.previewFeedback) {
    return IntegrationHealth.UNKNOWN;
  }

  const feedback = browserLocal.previewFeedback;

  // Check for errors in feedback
  if (feedback.errors && feedback.errors.length > 0) {
    return IntegrationHealth.ERROR;
  }

  // Check results_target for classic/combined modes
  if (feedback.results_target) {
    const { status, required } = feedback.results_target;
    const resultsTargetRequired = typeof required === 'boolean'
      ? required
      : mode === 'classic' || mode === 'combined';

    if (resultsTargetRequired
      && (status === 'missing_container' || status === 'route_404' || status === 'runtime_error')) {
      return IntegrationHealth.ERROR;
    }
  }

  // Check for warnings
  if (feedback.warnings && feedback.warnings.length > 0) {
    return IntegrationHealth.WARNING;
  }

  // Check search_input
  if (feedback.search_input && feedback.search_input.found === false) {
    return IntegrationHealth.WARNING;
  }

  // All checks passed
  if (feedback.search_input && feedback.search_input.found === true) {
    return IntegrationHealth.HEALTHY;
  }

  return IntegrationHealth.UNKNOWN;
}

function deriveDiagnosticsSeverity(state) {
  const errorFields = [
    state.connection === ConnectionStatus.ERROR,
    state.indexing === IndexingStatus.ERROR,
    state.previewBuild === PreviewBuildStatus.ERROR,
    state.previewBrowser === PreviewBrowserStatus.ERROR,
    state.live === LiveStatus.ERROR,
    state.integrationHealth === IntegrationHealth.ERROR,
  ];
  if (errorFields.some(Boolean)) return DiagnosticsSeverity.ERROR;

  const warningFields = [
    state.integrationHealth === IntegrationHealth.WARNING,
  ];
  if (warningFields.some(Boolean)) return DiagnosticsSeverity.WARNING;

  return DiagnosticsSeverity.NONE;
}

// ---------------------------------------------------------------------------
// Main composer
// ---------------------------------------------------------------------------

export function composeOperatorState(plugin, browserLocal, gateway) {
  const connection = deriveConnection(plugin);
  const indexing = deriveIndexing(plugin);
  const mode = deriveMode(plugin, gateway);
  const previewBuild = derivePreviewBuild(plugin);
  const previewBrowser = derivePreviewBrowser(browserLocal);
  const live = deriveLive(plugin, gateway, connection);
  const integrationHealth = deriveIntegrationHealth(mode, browserLocal);

  const partial = {
    connection,
    indexing,
    mode,
    previewBuild,
    previewBrowser,
    preview: previewBuild, // backward compat alias for existing consumers
    live,
    integrationHealth,
    diagnosticsSeverity: DiagnosticsSeverity.NONE,
    nextAction: null,
    publishState: plugin.publishState ?? null,
    onboarding: plugin.onboarding ?? null,
  };

  partial.diagnosticsSeverity = deriveDiagnosticsSeverity(partial);
  partial.nextAction = computeNextAction(partial);

  return partial;
}
